import clone from 'lodash/clone';
import { EventNames, trackEvent } from '../../lib/analytics';
import {
  customErrorEvents,
  flowPlayerErrorEvents,
  flowPlayerUIClasses,
  videoPlaybackEvents,
} from './VideoPlayerConstants';

export const parseConf = (conf: Record<string, unknown>) => {
  if (!conf) return {};
  const { key } = conf;
  return {
    fc_session_id: key,
    session_id: key,
  };
};

const getLastCuepoint = (cuepoints: any[]) => {
  return cuepoints.reduce((totalObj, current) => {
    const { index } = current;
    const totalIndex = totalObj.index;
    return index && index > totalIndex ? current : totalObj;
  });
};

export const parseCuepoints = (cuepoints: any) => {
  if (!cuepoints || !cuepoints.length) return 'N/A';
  const lastCuepoint = getLastCuepoint(cuepoints);
  return lastCuepoint.index || -1;
};

export const parseEngine = (engine: Record<string, any>) => {
  if (!engine) return {};
  const { hlsjs } = engine;
  const { levelController } = hlsjs || {};
  const { _level, _levels } = levelController || {};
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  // eslint-disable-next-line no-underscore-dangle
  const { attrs, bitrate } = _levels && _levels[_level] ? _levels[_level] : {};
  const { RESOLUTION } = attrs || {};
  return {
    bitrate,
    quality: RESOLUTION,
  };
};

export const parseError = (error: Record<string, unknown>) => {
  if (!error) return {};
  const { code, details } = error || {};
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  const errorMsg = flowPlayerErrorEvents[code] || details || customErrorEvents.UNREGISTERED_ERROR;
  return {
    properties: clone(error),
    method: errorMsg,
  };
};

export const parseVideoObject = (video: Record<string, unknown>) => {
  if (!video) return {};
  const { buffer, duration, time } = video;
  return {
    buffer,
    position: time,
    total_length: duration,
  };
};

export const parsePlayer = (player: Record<string, any>) => {
  if (!player) return {};
  const { conf, cuepoints, engine, isFullscreen, video, volumeLevel } = player;
  const parsedConf = parseConf(conf);
  const parsedEngine = parseEngine(engine);
  const parsedVideoObject = parseVideoObject(video);
  return {
    ...parsedConf,
    ...parsedEngine,
    ...parsedVideoObject,
    cue_point: parseCuepoints(cuepoints),
    framerate: 30,
    full_screen: isFullscreen,
    sound: volumeLevel,
  };
};

export const trackVideoEvent = (type: any, player: any, trackObj = {}, error?: any) => {
  try {
    const playerData = parsePlayer(player);
    const errorData = parseError(error);
    const trackData = { ...playerData, ...errorData, ...trackObj };
    return trackEvent(type, trackData);
  } catch (e) {
    console.error(videoPlaybackEvents.ERROR, e);
  }
};

const getInteractionEvent = (
  eventClassName: string,
  player: Record<string, any>,
): EventNames | null => {
  const { BACK_TEN, FORWARD_TEN, PLAY, ROOT, SCRUB_BACKWARD, SCRUB_FORWARD } = flowPlayerUIClasses;
  const { playing } = player || {};
  switch (eventClassName) {
    case PLAY:
    case ROOT:
      return playing ? EventNames.VIDEO_PLAYBACK_PAUSED : null;
      break;
    case SCRUB_BACKWARD:
      return EventNames.VIDEO_PLAYER_SCRUB_BACKWARD;
    case SCRUB_FORWARD:
      return EventNames.VIDEO_PLAYER_SCRUB_FORWARD;
    case BACK_TEN:
      return EventNames.VIDEO_PLAYER_BACK_10;
    case FORWARD_TEN:
      return EventNames.VIDEO_PLAYER_FORWARD_10;
    default:
      return null;
  }
};

export const trackInteractionEvent = (event: Record<string, any>, player: any, trackObj = {}) => {
  const { target } = event || {};
  const { className } = target || {};
  const interactionEvent = getInteractionEvent(className, player);
  if (interactionEvent) {
    return trackVideoEvent(interactionEvent, player, trackObj);
  }
};

export const trackSpeedChange = (trackData: Record<string, any>) => {
  return (prevSpeed: number, newSpeed: number) => {
    /* eslint-disable-next-line @typescript-eslint/camelcase */
    const trackDataWithSpeeds = {
      ...trackData,
      to_speed: newSpeed,
      from_speed: prevSpeed,
    };
    trackEvent(EventNames.VIDEO_PLAYER_SPEED_CHANGED, trackDataWithSpeeds);
  };
};

export const getVideoSpeedTrackData = (lesson?: Record<string, any>) => {
  /* eslint-disable @typescript-eslint/camelcase */
  if (!lesson) return {};
  const { id: activity_id, slug: activity_slug, title: activity_title, fc_id } = lesson;
  return {
    activity_id,
    activity_slug,
    activity_title,
    fc_id,
  };
  /* eslint-enable @typescript-eslint/camelcase */
};
